import React, { useState } from 'react';
import { Calculator, TrendingUp, DollarSign, Calendar, Sparkles, CheckCircle2, ArrowRight } from 'lucide-react';
import { useQuote } from '../context/QuoteContext';
import { useProducts } from '../context/ProductContext';
import { SafeImage } from './SafeImage';
import { formatKsh } from '../utils/formatters';

type MachineType = 'milk-atm' | 'water-station' | 'cooking-oil';

export const RoiCalculator: React.FC = () => {
  const { addToQuote } = useQuote();
  const { products } = useProducts();
  const [machineType, setMachineType] = useState<MachineType>('milk-atm');

  // Parameters for Milk ATM
  const [milkDailyLiters, setMilkDailyLiters] = useState(120);
  const [milkBuyPrice, setMilkBuyPrice] = useState(52); // KSh per liter from farmer / co-op
  const [milkSellPrice, setMilkSellPrice] = useState(75); // KSh per liter dispensed
  const [milkMonthlyRentPower, setMilkMonthlyRentPower] = useState(15000); // KSh monthly overhead

  // Parameters for Water Refill Kiosk
  const [waterDailyBottles, setWaterDailyBottles] = useState(70); // 20-Litre bottles
  const [waterRefillPrice, setWaterRefillPrice] = useState(40); // KSh per 20L refill
  const [waterCostPerBottle, setWaterCostPerBottle] = useState(8); // Water utility + filter replacement per bottle
  const [waterMonthlyRentPower, setWaterMonthlyRentPower] = useState(12000);

  // Parameters for Cooking Oil ATM
  const [oilDailyLiters, setOilDailyLiters] = useState(45);
  const [oilBuyPrice, setOilBuyPrice] = useState(190); // KSh per liter wholesale
  const [oilSellPrice, setOilSellPrice] = useState(250); // KSh per liter retail
  const [oilMonthlyOverhead, setOilMonthlyOverhead] = useState(8000);

  // Calculations
  let machineInvestmentKsh = 75000;
  let dailyGrossRevenue = 0;
  let dailyDirectCost = 0;
  let monthlyOverhead = 0;
  let associatedProductId = 'milk-atm-100l';

  if (machineType === 'milk-atm') {
    machineInvestmentKsh = 75000; // Milk ATM 100L
    associatedProductId = 'milk-atm-100l';
    dailyGrossRevenue = milkDailyLiters * milkSellPrice;
    dailyDirectCost = milkDailyLiters * milkBuyPrice;
    monthlyOverhead = milkMonthlyRentPower;
  } else if (machineType === 'water-station') {
    machineInvestmentKsh = 75000; // 3 Taps Water Filling Station
    associatedProductId = 'water-filling-station-3taps';
    dailyGrossRevenue = waterDailyBottles * waterRefillPrice;
    dailyDirectCost = waterDailyBottles * waterCostPerBottle;
    monthlyOverhead = waterMonthlyRentPower;
  } else {
    machineInvestmentKsh = 65000; // Cooking Oil ATM 50L
    associatedProductId = 'cooking-oil-atm-50l';
    dailyGrossRevenue = oilDailyLiters * oilSellPrice;
    dailyDirectCost = oilDailyLiters * oilBuyPrice;
    monthlyOverhead = oilMonthlyOverhead;
  }

  const dailyGrossMargin = dailyGrossRevenue - dailyDirectCost;
  const monthlyGrossMargin = dailyGrossMargin * 30;
  const monthlyNetProfit = Math.max(0, monthlyGrossMargin - monthlyOverhead);
  const paybackMonths = monthlyNetProfit > 0 ? (machineInvestmentKsh / monthlyNetProfit).toFixed(1) : 'N/A';
  const annualNetProfit = monthlyNetProfit * 12;

  const targetProduct = products.find((p) => p.id === associatedProductId);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header bar */}
      <div className="bg-slate-900 text-white p-6 sm:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sky-400 text-xs font-semibold uppercase tracking-wider mb-1">
              <Calculator className="w-4 h-4" />
              Interactive Investment Estimator
            </div>
            <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-white">
              Vending Machine ROI & Profitability Calculator
            </h3>
            <p className="text-slate-400 text-xs sm:text-sm mt-1 max-w-xl">
              Model your daily cashflow, monthly margins, and payback period for a Saints Tech Solutions automated kiosk in Kenya.
            </p>
          </div>

          {/* Segmented Machine Type Switcher */}
          <div className="flex bg-slate-800 p-1 rounded-xl shrink-0 border border-slate-700">
            <button
              onClick={() => setMachineType('milk-atm')}
              className={`px-3 py-2 text-xs font-semibold rounded-lg transition-colors cursor-pointer ${
                machineType === 'milk-atm'
                  ? 'bg-sky-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Milk ATM (100L)
            </button>
            <button
              onClick={() => setMachineType('water-station')}
              className={`px-3 py-2 text-xs font-semibold rounded-lg transition-colors cursor-pointer ${
                machineType === 'water-station'
                  ? 'bg-sky-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Water Station (3-Tap)
            </button>
            <button
              onClick={() => setMachineType('cooking-oil')}
              className={`px-3 py-2 text-xs font-semibold rounded-lg transition-colors cursor-pointer ${
                machineType === 'cooking-oil'
                  ? 'bg-sky-600 text-white shadow-xs'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Cooking Oil ATM
            </button>
          </div>
        </div>
      </div>

      {/* Main Grid: Inputs vs Real-time Projections */}
      <div className="p-6 sm:p-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Column: Sliders & Variables */}
        <div className="lg:col-span-7 space-y-6">
          <h4 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
            <span>Operating Variables (Adjust Below)</span>
          </h4>

          {machineType === 'milk-atm' && (
            <div className="space-y-5">
              <div>
                <div className="flex justify-between text-xs font-medium text-slate-700 mb-1.5">
                  <span>Daily Milk Volume Sold</span>
                  <span className="font-bold text-sky-700 tabular-nums">{milkDailyLiters} Litres / Day</span>
                </div>
                <input
                  type="range"
                  min="40"
                  max="400"
                  step="10"
                  value={milkDailyLiters}
                  onChange={(e) => setMilkDailyLiters(Number(e.target.value))}
                  className="w-full accent-sky-600 cursor-pointer"
                />
                <div className="flex justify-between text-[11px] text-slate-400">
                  <span>40 L (Low traffic)</span>
                  <span>120 L (Standard kiosk)</span>
                  <span>400 L (Prime market)</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Farmer / Co-op Buy Price (KSh/L)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500">KSh</span>
                    <input
                      type="number"
                      value={milkBuyPrice}
                      onChange={(e) => setMilkBuyPrice(Number(e.target.value))}
                      className="w-full text-sm font-bold px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-800"
                    />
                  </div>
                </div>

                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    ATM Retail Selling Price (KSh/L)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500">KSh</span>
                    <input
                      type="number"
                      value={milkSellPrice}
                      onChange={(e) => setMilkSellPrice(Number(e.target.value))}
                      className="w-full text-sm font-bold px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-800"
                    />
                  </div>
                </div>
              </div>

              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Estimated Monthly Shop Rent + Electricity + Attendant (KSh)
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-500">KSh</span>
                  <input
                    type="number"
                    value={milkMonthlyRentPower}
                    onChange={(e) => setMilkMonthlyRentPower(Number(e.target.value))}
                    className="w-full text-sm font-bold px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-800"
                  />
                </div>
              </div>
            </div>
          )}

          {machineType === 'water-station' && (
            <div className="space-y-5">
              <div>
                <div className="flex justify-between text-xs font-medium text-slate-700 mb-1.5">
                  <span>Daily 20-Litre Bottles Refilled</span>
                  <span className="font-bold text-sky-700 tabular-nums">{waterDailyBottles} Bottles / Day</span>
                </div>
                <input
                  type="range"
                  min="20"
                  max="250"
                  step="5"
                  value={waterDailyBottles}
                  onChange={(e) => setWaterDailyBottles(Number(e.target.value))}
                  className="w-full accent-sky-600 cursor-pointer"
                />
                <div className="flex justify-between text-[11px] text-slate-400">
                  <span>20 Bottles</span>
                  <span>70 Bottles (Average)</span>
                  <span>250 Bottles (High-density)</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Selling Price per 20L Bottle (KSh)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500">KSh</span>
                    <input
                      type="number"
                      value={waterRefillPrice}
                      onChange={(e) => setWaterRefillPrice(Number(e.target.value))}
                      className="w-full text-sm font-bold px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-800"
                    />
                  </div>
                </div>

                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Water Raw Utility + Power Cost (KSh/20L)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500">KSh</span>
                    <input
                      type="number"
                      value={waterCostPerBottle}
                      onChange={(e) => setWaterCostPerBottle(Number(e.target.value))}
                      className="w-full text-sm font-bold px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-800"
                    />
                  </div>
                </div>
              </div>

              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Monthly Space Rent & Operations (KSh)
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-500">KSh</span>
                  <input
                    type="number"
                    value={waterMonthlyRentPower}
                    onChange={(e) => setWaterMonthlyRentPower(Number(e.target.value))}
                    className="w-full text-sm font-bold px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-800"
                  />
                </div>
              </div>
            </div>
          )}

          {machineType === 'cooking-oil' && (
            <div className="space-y-5">
              <div>
                <div className="flex justify-between text-xs font-medium text-slate-700 mb-1.5">
                  <span>Daily Liters of Oil Dispensed</span>
                  <span className="font-bold text-sky-700 tabular-nums">{oilDailyLiters} Litres / Day</span>
                </div>
                <input
                  type="range"
                  min="15"
                  max="150"
                  step="5"
                  value={oilDailyLiters}
                  onChange={(e) => setOilDailyLiters(Number(e.target.value))}
                  className="w-full accent-sky-600 cursor-pointer"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Bulk Purchase Cost (KSh/L)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500">KSh</span>
                    <input
                      type="number"
                      value={oilBuyPrice}
                      onChange={(e) => setOilBuyPrice(Number(e.target.value))}
                      className="w-full text-sm font-bold px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-800"
                    />
                  </div>
                </div>

                <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Dispensed Retail Price (KSh/L)
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500">KSh</span>
                    <input
                      type="number"
                      value={oilSellPrice}
                      onChange={(e) => setOilSellPrice(Number(e.target.value))}
                      className="w-full text-sm font-bold px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-800"
                    />
                  </div>
                </div>
              </div>

              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Monthly Overhead (KSh)
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-500">KSh</span>
                  <input
                    type="number"
                    value={oilMonthlyOverhead}
                    onChange={(e) => setOilMonthlyOverhead(Number(e.target.value))}
                    className="w-full text-sm font-bold px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-slate-800"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Profit Scoreboard & Machine Link */}
        <div className="lg:col-span-5 bg-gradient-to-b from-slate-900 to-slate-950 text-white rounded-2xl p-6 sm:p-7 shadow-lg border border-slate-800 space-y-6">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-sky-400">
              Projected Earnings Breakdown
            </span>
            <div className="mt-2 flex items-baseline justify-between">
              <div>
                <div className="text-xs text-slate-400">Estimated Net Monthly Profit</div>
                <div className="text-3xl font-extrabold text-emerald-400 tabular-nums">
                  KSh {formatKsh(monthlyNetProfit)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-400">Payback Period</div>
                <div className="text-xl font-bold text-sky-300 tabular-nums">
                  {paybackMonths} Months
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown cards */}
          <div className="space-y-2.5 text-xs">
            <div className="flex justify-between py-2 border-b border-slate-800 text-slate-300">
              <span>Gross Daily Turnover</span>
              <span className="tabular-nums font-semibold text-white">
                KSh {formatKsh(dailyGrossRevenue)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-800 text-slate-300">
              <span>Gross Daily Margin</span>
              <span className="tabular-nums font-semibold text-emerald-400">
                +KSh {formatKsh(dailyGrossMargin)} / day
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-800 text-slate-300">
              <span>Monthly Fixed Overheads</span>
              <span className="tabular-nums text-slate-400">
                -KSh {formatKsh(monthlyOverhead)}
              </span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-800 text-slate-300">
              <span>Estimated Annual Net</span>
              <span className="tabular-nums font-bold text-sky-400 text-sm">
                KSh {formatKsh(annualNetProfit)} / year
              </span>
            </div>
          </div>

          {/* Machine link & direct quote trigger */}
          {targetProduct && (
            <div className="p-4 bg-slate-800/80 rounded-xl border border-slate-700/80 space-y-3">
              <div className="flex items-center gap-3">
                <SafeImage
                  src={targetProduct.image}
                  fallbackCategory={targetProduct.category}
                  alt={targetProduct.name}
                  className="w-12 h-12 rounded-lg object-cover bg-slate-700 shrink-0"
                />
                <div className="min-w-0">
                  <div className="text-xs text-slate-400">Recommended Model</div>
                  <div className="text-xs font-bold text-white truncate">
                    {targetProduct.name}
                  </div>
                  <div className="text-xs font-semibold text-sky-400 tabular-nums">
                    KSh {formatKsh(targetProduct.priceKsh)}
                  </div>
                </div>
              </div>

              <button
                onClick={() => addToQuote(targetProduct, 1)}
                className="w-full py-2.5 px-4 bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold rounded-lg transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Add This Machine to Quote Basket</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="text-[11px] text-slate-400 leading-relaxed border-t border-slate-800 pt-3">
            * Estimates are indicative based on typical Kenyan urban retail locations. Actual yields depend on site footfall, sourcing agreements, and operating hours.
          </div>
        </div>
      </div>
    </div>
  );
};
